import canonicalize from 'canonicalize'
import { ArtDerFahrt } from '../models/ArtDerFahrt'
import { BefehlDto, StandortDto } from '../dtos'

export interface BefehlsnachrichtHash1Parts {
  id: string
  zugnummer: number
  artDerFahrt: ArtDerFahrt
  arbeitsplatzbezeichnung: string
  arbeitsplatzOrt: string
  externalUserDisplayName: string
}

export interface BefehlsnachrichtHash2Parts extends BefehlsnachrichtHash1Parts {
  befehle: BefehlDto[]
}

export interface BefehlsnachrichtStandortParts {
  id: string
  standort: StandortDto
}

export interface BefehlsnachrichtEindeutigeKennungParts {
  id: string
  eindeutigeKennung: string
}

export enum BfnStatus {
  ERSTELLT = 'ERSTELLT',
  SMS_IM_VERSAND = 'SMS_IM_VERSAND',
  SMS_VERSAND_ABGEBROCHEN = 'SMS_VERSAND_ABGEBROCHEN',
  SMS_ZUGESTELLT = 'SMS_ZUGESTELLT',
  ERSTABRUF_GESTARTET = 'ERSTABRUF_GESTARTET',
  STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH = 'STANDORTABGLEICH_DURCH_FDL_ERFORDERLICH',
  STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH = 'STANDORTBESTAETIGUNG_DURCH_TF_ERFORDERLICH',
  STANDORTEINGABE_AKZEPTIERT = 'STANDORTEINGABE_AKZEPTIERT',
  STANDORTABGLEICH_FEHLGESCHLAGEN = 'STANDORTABGLEICH_FEHLGESCHLAGEN',
  ABGERUFEN = 'ABGERUFEN',
  QUITTIERT = 'QUITTIERT',
  ERLEDIGT = 'ERLEDIGT',
  ABGEWIESEN = 'ABGEWIESEN',
  GELOESCHT = 'GELOESCHT',
  WIDERRUFEN = 'WIDERRUFEN'
}

export interface BefehlsnachrichtStatusParts {
  id: string
  visuellerStatus: string
  technischerStatus: BfnStatus
  statusTimestamps: {
    status: string
    time: string
    visuellerStatus: string
  }[]
}

export interface BefehlsnachrichtStatusKorrekturParts {
  id: string
  statusKorrektur: string
}

/**
 * Creates a canonical JSON string and hashes it using SHA-256 with the Web Crypto API.
 *
 * Canonicalization is the process of converting data that has more than one possible representation
 * into a "standard" canonical representation. This ensures that the data is serialized in a consistent
 * way, which is crucial for cryptographic operations such as hashing and signing.
 *
 * This implementation follows the JSON Canonicalization Scheme (JCS) as specified in RFC 8785.
 *
 * @param befehlsnachrichtParts The parts of Befehlsnachricht to be canonicalized and hashed.
 * @returns A promise that resolves to a hexadecimal string of the SHA-256 hash.
 */
export async function createBefehlsnachrichtHash(
  befehlsnachrichtParts:
    | BefehlsnachrichtHash1Parts
    | BefehlsnachrichtHash2Parts
    | BefehlsnachrichtStandortParts
    | BefehlsnachrichtEindeutigeKennungParts
    | BefehlsnachrichtStatusParts
): Promise<string> {
  const canonicalJson = canonicalize(befehlsnachrichtParts)
  if (!canonicalJson) {
    throw new Error('Failed to canonicalize BefehlsnachrichtHashParts.')
  }

  const encoder = new TextEncoder()
  const data = encoder.encode(canonicalJson)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  return hashArray.map((byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Verifies if a given Befehlsnachricht object matches a given SHA-256 hash.
 *
 * This function ensures that the Befehlsnachricht object is first canonicalized using the same process as the
 * createBefehlsnachrichtHash function, and then hashed. It compares the resulting hash with the provided hash.
 * This guarantees that the comparison is done on a consistent and canonical representation of the JSON data.
 *
 * @param befehlsnachrichtParts The parts of Befehlsnachricht to be verified.
 * @param hash The SHA-256 hash to compare against.
 * @returns A promise that resolves to a boolean indicating whether the hash matches the Befehlsnachricht object.
 */
export async function verifyBefehlsnachrichtHash(
  befehlsnachrichtParts:
    | BefehlsnachrichtHash1Parts
    | BefehlsnachrichtHash2Parts
    | BefehlsnachrichtStandortParts
    | BefehlsnachrichtEindeutigeKennungParts
    | BefehlsnachrichtStatusParts,
  hash: string
): Promise<boolean> {
  const calculatedHash = await createBefehlsnachrichtHash(befehlsnachrichtParts)
  return calculatedHash === hash
}
